import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { ApiResponse, Workflow, CreateWorkflowRequest, NodeTypeDef, DataFile, Intent, Plan, LicenseStatusResponse, MachineFingerprint } from '../types';

// 创建 axios 实例
const apiClient: AxiosInstance = axios.create({
  baseURL: (import.meta as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL || '/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器 - 添加 token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 响应拦截器 - 统一错误处理
apiClient.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API 方法封装
const api = {
  // GET 请求
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    apiClient.get<unknown, ApiResponse<T>>(url, config),

  // POST 请求
  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.post<unknown, ApiResponse<T>>(url, data, config),

  // PUT 请求
  put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.put<unknown, ApiResponse<T>>(url, data, config),

  // DELETE 请求
  delete: <T>(url: string, config?: AxiosRequestConfig) =>
    apiClient.delete<unknown, ApiResponse<T>>(url, config),
};

// Workflow API
export const workflowApi = {
  // 获取节点类型列表
  getNodeTypes: () => api.get<NodeTypeDef[]>('/node-types'),

  // 获取工作流列表
  listWorkflows: () => api.get<{ workflows: Workflow[]; total: number }>('/workflows'),

  // 获取工作流详情
  getWorkflow: (id: string) => api.get<Workflow>(`/workflows/${id}`),

  // 创建工作流
  createWorkflow: (data: CreateWorkflowRequest) => api.post<Workflow>('/workflows', data),

  // 更新工作流
  updateWorkflow: (id: string, data: Partial<CreateWorkflowRequest>) =>
    api.put<Workflow>(`/workflows/${id}`, data),

  // 删除工作流
  deleteWorkflow: (id: string) => api.delete<void>(`/workflows/${id}`),

  // 验证工作流
  validateWorkflow: (data: CreateWorkflowRequest) =>
    api.post<{ valid: boolean }>('/workflows/validate', data),

  // 执行工作流（传 variables，结果由后端异步处理）
  executeWorkflow: (id: string, variables?: Record<string, unknown>) =>
    api.post<{ workflowId: string; status: string }>(`/workflows/${id}/execute`, { variables }),

  // 预览节点结果
  previewNode: (workflowId: string, nodeId: string) =>
    api.get<unknown>(`/workflows/${workflowId}/preview/${nodeId}`),

  // 应用工作流：上传文件并执行，返回结果预览数据
  // 支持多文件（每个输入节点独立上传），使用 /workflows/run 端点
  runWorkflow: async (
    workflowId: string,
    inputFiles: Array<{ nodeId: string; file: File }>,
    workflow?: { 
      name: string; 
      description?: string; 
      nodes: Array<{
        id: string;
        type: string;
        position: { x: number; y: number };
        data: {
          label: string;
          config: unknown;
        };
      }>; 
      edges: Array<{
        id: string;
        source: string;
        target: string;
      }>;
    }
  ): Promise<{
    columns: string[];
    rows: Record<string, unknown>[];
    rowCount: number;
    metadata?: Record<string, unknown>;
    /** Present when the terminal node is a group_by (or downstream of one without an append). */
    is_grouped?: boolean;
    /** One entry per group, ordered by first occurrence of the group key. */
    grouped_results?: Array<{
      group_key: string;
      columns: string[];
      rows: Record<string, unknown>[];
      rowCount: number;
    }>;
  }> => {
    const formData = new FormData();

    // Append each input-node file with its node ID as the field name.
    // The first file is also appended as 'file' for single-file backend compatibility.
    inputFiles.forEach(({ nodeId, file }, idx) => {
      formData.append(`file_${nodeId}`, file);
      if (idx === 0) formData.append('file', file);
    });

    // 如果提供了工作流定义，则作为 JSON 字符串传递
    if (workflow) {
      // 转换节点格式为后端期望的格式
      const convertedNodes = workflow.nodes.map(node => ({
        id: node.id,
        type: node.type,
        config: node.data.config as Record<string, unknown> || {},
        position: node.position,
      }));

      // 转换边格式为后端期望的格式 (source -> from, target -> to)
      const convertedEdges = workflow.edges.map(edge => ({
        from: edge.source,
        to: edge.target,
      }));

      formData.append('workflow', JSON.stringify({
        id: workflowId,
        name: workflow.name,
        description: workflow.description || '',
        nodes: convertedNodes,
        edges: convertedEdges,
      }));
    }

    const token = localStorage.getItem('token') || '';
    const baseURL =
      (import.meta as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL ||
      '/api/v1';

    // 使用新的 /workflows/run 端点（不带 workflowId）
    const url = workflow 
      ? `${baseURL}/workflows/run` 
      : `${baseURL}/workflows/${workflowId}/run`;

    const response = await fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || `HTTP ${response.status}`);
    }

    const json = await response.json() as {
      code: number;
      message: string;
      data: {
        columns: string[];
        rows: Record<string, unknown>[];
        rowCount: number;
        metadata?: Record<string, unknown>;
        is_grouped?: boolean;
        grouped_results?: Array<{
          group_key: string;
          columns: string[];
          rows: Record<string, unknown>[];
          rowCount: number;
        }>;
      };
    };

    // Backend uses code=0 for success (see pkg/response/response.go)
    if (json.code !== 0) {
      throw new Error(json.message || '工作流执行失败');
    }

    return json.data;
  },
};

// AI API
export const aiApi = {
  // 解析意图
  parseIntent: (input: string, context?: { availableTables: { name: string; columns: { name: string; type: string }[] }[] }) =>
    api.post<{ intent: Intent; plan?: Plan; workflow?: string }>('/ai/parse', { input, context }),

  // 生成执行计划
  generatePlan: (intent: Intent, tables?: { name: string; columns: { name: string; type: string }[] }[]) =>
    api.post<{ plan: Plan; workflow: string; validation: { valid: boolean; errors?: { field: string; message: string }[] } }>('/ai/plan', { intent, tables }),

  // 验证计划
  validatePlan: (plan: Plan) =>
    api.post<{ valid: boolean; errors?: { field: string; message: string; stepId?: string }[] }>('/ai/plan/validate', { plan }),

  // 确认计划
  confirmPlan: (planId: string, confirm: boolean, feedback?: string) =>
    api.post<{ confirmed: boolean; workflowId?: string; message: string }>('/ai/plan/confirm', { planId, confirm, feedback }),

  // 获取建议
  getSuggestions: (query?: string) =>
    api.get<{ suggestions: { text: string; description: string }[] }>('/ai/suggestions', { params: { q: query } }),

  // 获取示例
  getExamples: () =>
    api.get<{ examples: { input: string; intent: string; plan: string; category: string }[] }>('/ai/examples'),
};

// Auth API
export const authApi = {
  // 登录
  login: (username: string, password: string) =>
    api.post<{
      user: {
        id: string;
        username: string;
        email?: string;
        role?: string;
      };
      tokens: {
        access_token: string;
        refresh_token: string;
        expires_at: number;
      };
      requires_password_change: boolean;
    }>('/auth/login', { username, password }),

  // 刷新token
  refreshToken: (refreshToken: string) =>
    api.post<{
      tokens: {
        access_token: string;
        refresh_token: string;
        expires_at: number;
      };
    }>('/auth/refresh', { refresh_token: refreshToken }),

  // 登出
  logout: () => api.post<void>('/auth/logout'),

  // 获取当前用户
  getCurrentUser: () =>
    api.get<{
      id: string;
      username: string;
      email?: string;
      role?: string;
    }>('/auth/me'),

  // 修改密码
  changePassword: (oldPassword: string, newPassword: string) =>
    api.post<void>('/auth/password', { old_password: oldPassword, new_password: newPassword }),

  // 获取微信登录授权URL
  getWeChatAuthURL: () =>
    api.get<{
      auth_url: string;
      state: string;
    }>('/auth/wechat/url'),

  // 微信登录回调
  weChatCallback: (code: string, state: string) =>
    api.post<{
      user: {
        id: string;
        username: string;
        email?: string;
        role?: string;
      };
      tokens: {
        access_token: string;
        refresh_token: string;
        expires_at: number;
      };
      requires_password_change: boolean;
    }>('/auth/wechat/callback', null, { params: { code, state } }),
};

// Subscription API
export const subscriptionApi = {
  // 获取账单历史
  getBillingHistory: () => api.get<Array<{
    id: string;
    userId: string;
    date: string;
    amount: number;
    status: string;
    plan?: string;
    createdAt: string;
  }>>('/subscription/billing'),

  // 获取当前订阅
  getCurrentSubscription: () => api.get<{
    id: string;
    userId: string;
    plan: 'free' | 'pro' | 'enterprise';
    status: 'active' | 'expired' | 'canceled' | 'pending';
    startedAt?: string;
    expiresAt?: string;
    maxWorkflowExecutions: number;
    maxStorageBytes: number;
    currentMonthExecutions: number;
    currentStorageBytes: number;
  }>('/subscription'),

  // 获取用量统计
  getUsageStats: () => api.get<{
    currentMonthExecutions: number;
    maxWorkflowExecutions: number;
    currentStorageBytes: number;
    maxStorageBytes: number;
    executionUsagePercent: number;
    storageUsagePercent: number;
  }>('/subscription/usage'),

  // 获取所有计划
  getAllPlans: () => api.get<{
    plan: 'free' | 'pro' | 'enterprise';
    name: string;
    maxWorkflowExecutions: number;
    maxStorageBytes: number;
    features: string[];
  }[]>('/subscription/plans'),

  // 升级订阅
  upgradeSubscription: (plan: 'free' | 'pro' | 'enterprise', durationMonths?: number) =>
    api.post<{
      id: string;
      plan: 'free' | 'pro' | 'enterprise';
      status: string;
      expiresAt?: string;
    }>('/subscription/upgrade', { plan, durationMonths }),

  // 取消订阅
  cancelSubscription: () => api.post<void>('/subscription/cancel'),

  // 续订
  renewSubscription: (durationMonths: number) =>
    api.post<{
      id: string;
      expiresAt?: string;
    }>('/subscription/renew', { durationMonths }),

  // 检查权限
  checkPermission: (action: 'execute_workflow' | 'store_data', bytes?: number) =>
    api.post<{ allowed: boolean; message: string }>('/subscription/check', { action, bytes }),

  // 微信支付委托代扣
  isWechatpayEnabled: () =>
    api.get<{ enabled: boolean }>('/subscription/wechatpay/enabled'),
  createWechatpaySign: (plan: 'pro' | 'enterprise') =>
    api.post<{ redirectUrl: string; preEntrustwebId: string; outContractCode: string }>(
      '/subscription/wechatpay/sign',
      { plan }
    ),
  terminateWechatpayContract: (plan: 'pro' | 'enterprise') =>
    api.post<void>('/subscription/wechatpay/terminate', { plan }),
  getWechatpayContractStatus: (plan: 'pro' | 'enterprise') =>
    api.get<{ signed: boolean; enabled: boolean; contractId?: string; signedAt?: string }>(
      `/subscription/wechatpay/contract?plan=${plan}`
    ),
};

// Data API
export const dataApi = {
  // 获取数据列表
  listData: () => api.get<{ data: DataFile[]; total: number }>('/data'),

  // 获取数据详情
  getData: (id: string) => api.get<DataFile>(`/data/${id}`),

  // 上传数据
  uploadData: (file: File, name?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (name) {
      formData.append('name', name);
    }
    return api.post<DataFile>('/data/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // 删除数据
  deleteData: (id: string) => api.delete<void>(`/data/${id}`),

  // 预览数据
  previewData: (id: string, limit?: number) =>
    api.get<{ data: Record<string, unknown>[] }>(`/data/${id}/preview`, { params: { limit } }),

  // 获取数据结构
  getDataSchema: (id: string) =>
    api.get<{ schema: { name: string; type: string; nullable: boolean; semanticType?: string }[]; statistics: { rowCount: number; columnCount: number; nullCounts: Record<string, number> } }>(`/data/${id}/schema`),

  // 导出数据
  exportData: (id: string, format: 'excel' | 'csv' = 'excel') =>
    api.post<{ downloadUrl: string }>(`/data/${id}/export`, { format }),
};

// License API (仅本地部署)
export const licenseApi = {
  // 获取授权状态
  getLicenseStatus: () => api.get<LicenseStatusResponse>('/license/status'),

  // 获取机器指纹
  getFingerprint: () => api.get<MachineFingerprint>('/license/fingerprint'),

  // 下载指纹采集文件
  downloadFingerprint: () => {
    const token = localStorage.getItem('token') || '';
    const baseURL =
      (import.meta as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL || '/api/v1';
    return fetch(`${baseURL}/license/download-custom-file`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    });
  },

  // 上传 License 文件
  uploadLicense: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<void>('/license/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // 获取授权功能列表
  getFeatures: () => api.get<{ features: string[] }>('/license/features'),

  // 刷新授权验证
  refreshLicense: () => api.post<void>('/license/refresh'),
};

// User Management API (仅本地部署，需要 admin 权限)
export const userApi = {
  // 获取用户列表
  listUsers: (params?: { page?: number; pageSize?: number; keyword?: string; status?: string }) =>
    api.get<{
      users: Array<{
        id: string;
        username: string;
        email?: string;
        avatar?: string;
        role?: string;
        status?: string;
        createdAt?: string;
      }>;
      total: number;
      page: number;
      pageSize: number;
    }>('/users', { params }),

  // 创建用户
  createUser: (data: {
    username: string;
    email: string;
    password: string;
    role: 'admin' | 'user';
  }) => api.post<{
    id: string;
    username: string;
    email: string;
    role: string;
  }>('/users', data),

  // 更新用户
  updateUser: (id: string, data: {
    email?: string;
    role?: 'admin' | 'user';
    status?: 'active' | 'inactive' | 'locked';
  }) => api.put<{
    id: string;
    username: string;
    email: string;
    role: string;
    status: string;
  }>(`/users/${id}`, data),

  // 删除用户
  deleteUser: (id: string) => api.delete<void>(`/users/${id}`),

  // 重置密码
  resetPassword: (id: string, newPassword: string) =>
    api.post<void>(`/users/${id}/reset-password`, { newPassword }),

  // 切换用户状态（停用/启用）
  toggleUserStatus: (id: string) =>
    api.post<{ id: string; status: string }>(`/users/${id}/toggle-status`),
};

export default api;
